// Reading a token's claims, never checking them: the API verifies, the package only needs to know
// whose a token is and what it may do.

/** The claims the package reads. Each is `undefined` when the token does not carry it as text. */
export interface TokenClaims {
  readonly sub: string | undefined
  readonly email: string | undefined
  readonly scope: string | undefined
}

/** The payload as it comes, before any claim is trusted to be what its name says. */
interface RawClaims {
  readonly sub?: unknown
  readonly email?: unknown
  readonly scope?: unknown
}

function textOf(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

/** The claims of a JWT, or null for anything that is not one. */
export function claimsOf(token: string): TokenClaims | null {
  const segment = token.split('.')[1] ?? ''
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
  try {
    const parsed = JSON.parse(
      atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')),
    ) as RawClaims | null
    if (typeof parsed !== 'object' || parsed === null) return null
    return { sub: textOf(parsed.sub), email: textOf(parsed.email), scope: textOf(parsed.scope) }
  } catch {
    return null
  }
}

/** A claim as text, or undefined when the token is unreadable, the claim absent or of another type. */
export function stringClaim(token: string, name: keyof TokenClaims): string | undefined {
  return claimsOf(token)?.[name]
}
